import React, { useContext, useEffect, useRef } from 'react';
import { Country, PersonalInformation } from "../../model/PersonalInformationType";
import { useNavigate } from 'react-router-dom';
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { CreateZahtjevOsobniPodaciRequest, useUpdatePersonalData } from "../../api/RegistrationApi";
import InputField from "../common/InputField";
import AutocompleteField from "../common/AutocompleteField";
import Switch from "../common/Switch";
import RadioGroup from "../common/RadioGroup";
import { Button } from "../common/Button";
import { Formik, FormikErrors, FormikValues } from "formik";
import { validateField, validateForm } from "./personalInformationValidation";
import { useNotification } from "../../common/context/NotificationProvider";
import { Card } from "../common/Card";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { toNullOrString } from "../../util/stringUtil";
import { PERSONAL_INFORMATION_STATUS } from "../../common/constants/navigationConstants";
import {
    DOCUMENT_TYPE_RADIO_GROUP_OPTIONS,
    COUNTRIES_DATA,
    GENDER_RADIO_GROUP_OPTIONS,
    UKRAJINA_OZNAKA,
} from "../../common/data/sifarnik";
import { Alert } from "@mui/material";
import { useOnScreen } from "../../common/hooks/useOnScreen";
import { DateInputField, getPaddedValue } from "../common/DateInputField";
import {
    getCountryById,
    getCountryDataWithCroatiaBeingFirst,
    getUkrajinaRegijaById,
    getUkrajinaRegijaOptions,
} from "../../util/sifarnikUtil";
import { useTranslation } from "react-i18next";
import { SupportedLanguage } from "../../i18n";
import { scrollToFirstError } from "../../util/formUtil";

const INITIAL_PERSONAL_INFORMATION_FORMIK_VALUES: PersonalInformation = {
    spol: "",
    ime: "",
    prezime: "",
    datumRodjenja: "",
    mjestoRodjenja: "",
    drzavaRodjenja: {} as Country,
    drzavaRodjenjaRegija: undefined,
    drzavljanstvo: {} as Country,
    drzavljanstvoRegija: undefined,
    imaDrugoDrzavljanstvo: false,
    drzavljanstvoDrugo: {} as Country,
    drzavljanstvoDrugoRegija: undefined,
    idDokumentVrsta: "osobna",
    idDokumentBroj: "",
    idDokumentIzdavatelj: "",
    idDokumentDrzavaIzdavanja: {} as Country,
    idDokumentDrzavaIzdavanjaRegija: undefined,
    idDokumentDatumVazenja: "",
    idDokumentTrajnoVazenje: false,
} as const;

const COUNTRY_TO_REGIJA_FIELD: Partial<Record<keyof PersonalInformation, keyof PersonalInformation>> = {
    drzavaRodjenja: "drzavaRodjenjaRegija",
    drzavljanstvo: "drzavljanstvoRegija",
    drzavljanstvoDrugo: "drzavljanstvoDrugoRegija",
    idDokumentDrzavaIzdavanja: "idDokumentDrzavaIzdavanjaRegija",
};

const isUkraine = (country?: Country | null): boolean => country?.oznaka === UKRAJINA_OZNAKA;

function mapPersonalInformationToCreateZahtjevOsobniPodaciRequest(input: PersonalInformation): CreateZahtjevOsobniPodaciRequest {
    return {
        spol: input.spol,
        ime: input.ime,
        prezime: input.prezime,
        datumRodjenja: input.datumRodjenja,
        mjestoRodjenja: input.mjestoRodjenja,
        drzavaRodjenja: input.drzavaRodjenja.oznaka,
        drzavaRodjenjaRegija: input.drzavaRodjenjaRegija?.oznaka || null,
        drzavljanstvo: input.drzavljanstvo.oznaka,
        drzavljanstvoRegija: input.drzavljanstvoRegija?.oznaka || null,
        imaDrugoDrzavljanstvo: input.imaDrugoDrzavljanstvo,
        drzavljanstvoDrugo: input.drzavljanstvoDrugo?.oznaka || null,
        drzavljanstvoDrugoRegija: input.drzavljanstvoDrugoRegija?.oznaka || null,
        idDokumentVrsta: input.idDokumentVrsta,
        idDokumentBroj: input.idDokumentBroj,
        idDokumentIzdavatelj: input.idDokumentIzdavatelj,
        idDokumentDrzavaIzdavanja: input.idDokumentDrzavaIzdavanja.oznaka,
        idDokumentDrzavaIzdavanjaRegija: input.idDokumentDrzavaIzdavanjaRegija?.oznaka || null,
        idDokumentDatumVazenja: toNullOrString(input.idDokumentDatumVazenja),
        idDokumentTrajnoVazenje: input.idDokumentTrajnoVazenje,
    }
}

const PersonalInformationPage = () => {
    const { t, i18n } = useTranslation();
    const lng = i18n.resolvedLanguage as SupportedLanguage;
    const {
        uploadInformation,
        personalInformation,
        personalInformationCallback,
        getZahtjevId,
        getStatus,
        restoreContext,
    } = useContext(ZahtjevContext);
    const { isPending: isLoading, mutate: updatePersonalDataMutation } = useUpdatePersonalData(getZahtjevId());
    const currentStatus = getStatus();
    const pageStatus = PERSONAL_INFORMATION_STATUS;
    const initalPersonalInformationFormValues = Object.keys(personalInformation).length !== 0
      ? personalInformation
      : {
          ...INITIAL_PERSONAL_INFORMATION_FORMIK_VALUES,
          idDokumentVrsta: uploadInformation.documentType,
      };
    const { notify } = useNotification();
    const navigate = useNavigate();
    const spolFieldRef = useRef(null)
    const isSpolFieldOutsideView = !useOnScreen(spolFieldRef, parseInt(currentStatus) >= parseInt(pageStatus), false);

    const handleSubmit = (values: PersonalInformation, setFieldError: (field: string, message: (string | undefined)) => void) => {
        values.datumRodjenja = getPaddedValue(values.datumRodjenja).slice(0, -1);
        if (values.idDokumentDatumVazenja) {
            values.idDokumentDatumVazenja = getPaddedValue(values.idDokumentDatumVazenja).slice(0, -1);
        }
        const isFormValid = validateForm(values, setFieldError);

        if (isFormValid) {
            const request = mapPersonalInformationToCreateZahtjevOsobniPodaciRequest(values);

            updatePersonalDataMutation(request, {
                onSuccess: () => {
                    personalInformationCallback(values);
                    navigate('/contact-address');
                },
                onError: () => {
                    notify(ERROR_MESSAGE_NOTIFICATION, "error");
                }
            });
        } else {
            scrollToFirstError();
        }
    };

    const handleValidation = (setFieldError: (field: string, message: (string | undefined)) => void) =>
      (value: any, field?: string) => {
          return validateField(field!, value, setFieldError)
      };

    const handleSetFieldValue = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
                                 setFieldError: (field: string, message: (string | undefined)) => void = () => {
                                 }) =>
      (value: any, field?: string) => {
          const currentField = field as keyof PersonalInformation;

          if (currentField === "imaDrugoDrzavljanstvo") {
              if (!(value as boolean)) {
                  setFieldValue("drzavljanstvoDrugo", null, false);
                  setFieldError("drzavljanstvoDrugo", undefined);
                  setFieldValue("drzavljanstvoDrugoRegija", undefined, false);
                  setFieldError("drzavljanstvoDrugoRegija", undefined);
              }
          } else if (currentField === "idDokumentTrajnoVazenje") {
              if ((value as boolean)) {
                  setFieldValue("idDokumentDatumVazenja", "", false);
                  setFieldError("idDokumentDatumVazenja", undefined);
              }
          }

          const regijaField = COUNTRY_TO_REGIJA_FIELD[currentField];
          if (regijaField && !isUkraine(value as Country | null)) {
              setFieldValue(regijaField, undefined, false);
              setFieldError(regijaField, undefined);
          }

          return setFieldValue(field!, value, false);
      };

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
      <Card title={t("personal-information-page.title")} isLoading={parseInt(currentStatus) < parseInt(pageStatus)} bodyClassName="md:w-full">
          <Formik
            initialValues={initalPersonalInformationFormValues}
            onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}
          >{({ values, errors, handleSubmit, setFieldValue, setFieldError }) => (
            <form noValidate onSubmit={handleSubmit} autoComplete="off">
                <div className="space-y-5">
                    <Alert className="mb-2 md:w-2/3" sx={{ alignItems: 'center' }} severity="info">
                        {t("common.alert-required-fields")}
                    </Alert>

                    <RadioGroup
                      name="idDokumentVrsta"
                      options={DOCUMENT_TYPE_RADIO_GROUP_OPTIONS.map(option => {
                        return {
                          ...option,
                          label: t(option.label)
                        }
                      })}
                      label={t("personal-information-page.identification-document.type-field.label")}
                      defaultValue={values.idDokumentVrsta}
                      error={t(errors.idDokumentVrsta as string)}
                      disabled={true}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                    />

                    <InputField
                      className="md:w-2/3"
                      name="idDokumentBroj"
                      label={t("personal-information-page.identification-document.number-field.label")}
                      maxLength={13}
                      defaultValue={values.idDokumentBroj}
                      error={t(errors.idDokumentBroj as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    <InputField
                      className="md:w-2/3"
                      name="idDokumentIzdavatelj"
                      label={t("personal-information-page.identification-document.issuer-field.label")}
                      maxLength={30}
                      defaultValue={values.idDokumentIzdavatelj}
                      error={t(errors.idDokumentIzdavatelj as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    <AutocompleteField
                      className="md:w-2/3"
                      name="idDokumentDrzavaIzdavanja"
                      label={t("personal-information-page.identification-document.issuer-state-field.label")}
                      options={getCountryDataWithCroatiaBeingFirst(lng)}
                      getOptionLabel={(option) => option?.drzava || ""}
                      isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                      defaultValue={values.idDokumentDrzavaIzdavanja ?
                        {
                        oznaka: values.idDokumentDrzavaIzdavanja.oznaka,
                        drzava: getCountryById(values.idDokumentDrzavaIzdavanja.oznaka, lng).drzava
                        }
                        : undefined
                      }
                      error={t(errors?.idDokumentDrzavaIzdavanja as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    {isUkraine(values.idDokumentDrzavaIzdavanja) &&
                      <AutocompleteField
                        className="md:w-2/3"
                        name="idDokumentDrzavaIzdavanjaRegija"
                        label={t("common.ukraine-region.label")}
                        options={getUkrajinaRegijaOptions(t)}
                        getOptionLabel={(option) => option?.drzava || ""}
                        isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                        defaultValue={values.idDokumentDrzavaIzdavanjaRegija?.oznaka
                          ? getUkrajinaRegijaById(values.idDokumentDrzavaIzdavanjaRegija.oznaka, t)
                          : undefined}
                        error={t(errors?.idDokumentDrzavaIzdavanjaRegija as string)}
                        handleValueChange={handleSetFieldValue(setFieldValue)}
                        handleValidation={handleValidation(setFieldError)}
                      />
                    }

                    {!values.idDokumentTrajnoVazenje &&
                      <DateInputField
                        className="md:w-2/3"
                        name="idDokumentDatumVazenja"
                        label={t("personal-information-page.identification-document.expiration-date-field.label")}
                        defaultValue={values.idDokumentDatumVazenja}
                        error={t(errors.idDokumentDatumVazenja as string)}
                        handleValueChange={handleSetFieldValue(setFieldValue)}
                        handleValidation={handleValidation(setFieldError)}
                      />
                    }

                    {(values.idDokumentVrsta === "osobna" && !values.idDokumentDatumVazenja) &&
                      <Switch
                        name="idDokumentTrajnoVazenje"
                        label={t("personal-information-page.identification-document.permanent-id-card-field.label")}
                        defaultValue={values.idDokumentTrajnoVazenje}
                        handleValueChange={handleSetFieldValue(setFieldValue, setFieldError)}
                      />
                    }

                    <RadioGroup
                      name="spol"
                      ref={spolFieldRef}
                      options={GENDER_RADIO_GROUP_OPTIONS.map(option => {
                        return {
                          ...option,
                          label: t(option.label)
                        }
                      })}
                      label={t("personal-information-page.personal-information.gender-field.label")}
                      defaultValue={values.spol}
                      error={t(errors.spol as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                      isOutsideView={isSpolFieldOutsideView}
                    />

                    <InputField
                      className="md:w-2/3"
                      name="ime"
                      label={t("personal-information-page.personal-information.name-field.label")}
                      maxLength={35}
                      defaultValue={values.ime}
                      error={t(errors.ime as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    <InputField
                      className="md:w-2/3"
                      name="prezime"
                      label={t("personal-information-page.personal-information.surname-field.label")}
                      maxLength={35}
                      defaultValue={values.prezime}
                      error={t(errors.prezime as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    <DateInputField
                      className="md:w-2/3"
                      name="datumRodjenja"
                      label={t("personal-information-page.personal-information.date-of-birth-field.label")}
                      defaultValue={values.datumRodjenja}
                      error={t(errors.datumRodjenja as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    <InputField
                      className="md:w-2/3"
                      name="mjestoRodjenja"
                      label={t("personal-information-page.personal-information.birth-place-field.label")}
                      maxLength={30}
                      defaultValue={values.mjestoRodjenja}
                      error={t(errors.mjestoRodjenja as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    <AutocompleteField
                      className="md:w-2/3"
                      name="drzavaRodjenja"
                      label={t("personal-information-page.personal-information.birth-state-field.label")}
                      options={getCountryDataWithCroatiaBeingFirst(lng)}
                      getOptionLabel={(option) => option?.drzava || ""}
                      isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                      defaultValue={values.drzavaRodjenja ?
                        {
                        oznaka: values.drzavaRodjenja.oznaka,
                        drzava: getCountryById(values.drzavaRodjenja.oznaka, lng).drzava
                        }
                        : undefined
                      }
                      error={t(errors?.drzavaRodjenja as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    {isUkraine(values.drzavaRodjenja) &&
                      <AutocompleteField
                        className="md:w-2/3"
                        name="drzavaRodjenjaRegija"
                        label={t("common.ukraine-region.label")}
                        options={getUkrajinaRegijaOptions(t)}
                        getOptionLabel={(option) => option?.drzava || ""}
                        isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                        defaultValue={values.drzavaRodjenjaRegija?.oznaka
                          ? getUkrajinaRegijaById(values.drzavaRodjenjaRegija.oznaka, t)
                          : undefined}
                        error={t(errors?.drzavaRodjenjaRegija as string)}
                        handleValueChange={handleSetFieldValue(setFieldValue)}
                        handleValidation={handleValidation(setFieldError)}
                      />
                    }

                    <AutocompleteField
                      className="md:w-2/3"
                      name="drzavljanstvo"
                      label={t("personal-information-page.personal-information.citizenship-field.label")}
                      options={getCountryDataWithCroatiaBeingFirst(lng)}
                      getOptionLabel={(option) => option?.drzava || ""}
                      isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                      defaultValue={values.drzavljanstvo ?
                        {
                          oznaka: values.drzavljanstvo.oznaka,
                          drzava: getCountryById(values.drzavljanstvo.oznaka, lng).drzava
                        }
                        : undefined
                      }
                      error={t(errors?.drzavljanstvo as string)}
                      handleValueChange={handleSetFieldValue(setFieldValue)}
                      handleValidation={handleValidation(setFieldError)}
                    />

                    {isUkraine(values.drzavljanstvo) &&
                      <AutocompleteField
                        className="md:w-2/3"
                        name="drzavljanstvoRegija"
                        label={t("common.ukraine-region.label")}
                        options={getUkrajinaRegijaOptions(t)}
                        getOptionLabel={(option) => option?.drzava || ""}
                        isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                        defaultValue={values.drzavljanstvoRegija?.oznaka
                          ? getUkrajinaRegijaById(values.drzavljanstvoRegija.oznaka, t)
                          : undefined}
                        error={t(errors?.drzavljanstvoRegija as string)}
                        handleValueChange={handleSetFieldValue(setFieldValue)}
                        handleValidation={handleValidation(setFieldError)}
                      />
                    }

                    <Switch
                      name="imaDrugoDrzavljanstvo"
                      label={t("personal-information-page.personal-information.dual-citizenship-field.label")}
                      defaultValue={values.imaDrugoDrzavljanstvo}
                      handleValueChange={handleSetFieldValue(setFieldValue, setFieldError)}
                    />

                    {values.imaDrugoDrzavljanstvo &&
                      <>
                        <AutocompleteField
                          className="md:w-2/3"
                          name="drzavljanstvoDrugo"
                          label={t("personal-information-page.personal-information.second-citizenship-field.label")}
                          options={COUNTRIES_DATA(lng)}
                          getOptionLabel={(option) => option?.drzava ?? ""}
                          isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                          defaultValue={values.drzavljanstvoDrugo ?
                            {
                              oznaka: values.drzavljanstvoDrugo.oznaka,
                              drzava: getCountryById(values.drzavljanstvoDrugo.oznaka, lng).drzava
                            }
                            : undefined
                          }
                          error={t(errors?.drzavljanstvoDrugo as string)}
                          handleValueChange={handleSetFieldValue(setFieldValue)}
                          handleValidation={handleValidation(setFieldError)}
                        />

                        {isUkraine(values.drzavljanstvoDrugo) &&
                          <AutocompleteField
                            className="md:w-2/3"
                            name="drzavljanstvoDrugoRegija"
                            label={t("common.ukraine-region.label")}
                            options={getUkrajinaRegijaOptions(t)}
                            getOptionLabel={(option) => option?.drzava || ""}
                            isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                            defaultValue={values.drzavljanstvoDrugoRegija?.oznaka
                              ? getUkrajinaRegijaById(values.drzavljanstvoDrugoRegija.oznaka, t)
                              : undefined}
                            error={t(errors?.drzavljanstvoDrugoRegija as string)}
                            handleValueChange={handleSetFieldValue(setFieldValue)}
                            handleValidation={handleValidation(setFieldError)}
                          />
                        }
                      </>
                    }

                                    </div>

                <div className="mt-10 text-center md:text-left">
                    <Button
                      type="submit"
                      text={t("common.button-next")}
                      disabled={isLoading}
                    />
                </div>
            </form>
          )}
          </Formik>
      </Card>
    );
};

export default PersonalInformationPage;
